import { useEffect, useState } from 'react';

import { Box, Button, Flex, HStack, Heading, VStack } from '@chakra-ui/react';
import { EntrySys } from 'contentful';
import Link from 'next/link';
import { useRouter } from 'next/router';

import style from './NewsSection.style';

import {
  Container,
  ContentfulRichText,
  Icon,
  Image,
  Section,
} from '@/components';
import { getArticleIds } from '@/graphql/api';
import useTranslation from '@/hooks/useTranslate';
import { AssetType, RichTextType } from '@/types/ContentFul.type';

interface NewsSectionProps {
  sys: EntrySys;
  fields: {
    title: string;
    description: RichTextType;
    isFeatured: boolean;
    image: AssetType;
  };
  id?: string;
}

export default function NewsSection(props: NewsSectionProps) {
  const {
    // sys: { updatedAt },
    fields: { title, description, image },
    id,
  } = props;
  /* const { locale } = useContext(Context);
  const localeFormat = locale === 'en' ? 'en-US' : 'ar-EG';
  const publishedAt = new Intl.DateTimeFormat(localeFormat, {
    dateStyle: locale === 'en' ? 'medium' : 'long',
  }).format(new Date(updatedAt)); */
  const router = useRouter();
  const [articles, setArticles] = useState<{ sys: { id: string } }[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    getArticleIds().then(articles => {
      setArticles(articles);
    });
  }, []);

  const currentIdx = articles.findIndex(
    article => article.sys.id === router.query.id
  );

  const isFirstItem = currentIdx === 0;
  const isLastItem = currentIdx === articles.length - 1;
  let nextArticleId = articles[currentIdx + 1]?.sys?.id || '';
  let prevArticleId = articles[currentIdx - 1]?.sys?.id || '';
  const isFirstItemDisabled = (isFirstItem && isLastItem) || isFirstItem;
  const isLastItemDisabled = (isFirstItem && isLastItem) || isLastItem;

  if (nextArticleId && typeof nextArticleId !== 'string') {
    nextArticleId = '';
  }
  if (prevArticleId && typeof prevArticleId !== 'string') {
    prevArticleId = '';
  }

  return (
    <Section sx={style.root} id={id}>
      <Box className="news-section-header-container">
        <Image
          loaderOpt={{ w: 1920, h: 770, fit: 'fill' }}
          src={image.fields.file.url}
          alt={image.fields.title}
          className="news-section-image"
          fill
        />
        <Container>
          <VStack className="news-section-heading-wrapper">
            <Heading as="h1" className="news-section-heading">
              {title}
            </Heading>
            {/* <Text fontSize={FontSizes.normal}>{publishedAt}</Text> */}
          </VStack>
        </Container>
      </Box>

      <Flex className="news-section-body">
        <Flex className="news-section-body-inner">
          <ContentfulRichText document={description} />
          <HStack className="news-section-button-wrap">
            <Button
              variant="secondary"
              as={!isFirstItemDisabled ? Link : undefined}
              href={
                !isFirstItemDisabled
                  ? `/support/articles/${prevArticleId}`
                  : undefined
              }
              isDisabled={isFirstItemDisabled}
              leftIcon={
                <Icon
                  name="arrowBack"
                  className="news-section-icon-arrowBack"
                />
              }
              className="news-section-button-prev"
            >
              {t('prev_article')}
            </Button>
            <Button
              as={!isLastItemDisabled ? Link : undefined}
              href={
                !isLastItemDisabled
                  ? `/support/articles/${nextArticleId}`
                  : undefined
              }
              isDisabled={isLastItemDisabled}
              rightIcon={
                <Icon name="arrow" className="news-section-icon-arrow" />
              }
              className="news-section-button-next"
            >
              {t('next_article')}
            </Button>
          </HStack>
        </Flex>
      </Flex>
    </Section>
  );
}
